import { useMemo } from 'react';
import { Lesson } from '../../../api/lessons.api';
import { termWeekCount, weekInLessonWindow } from '../../../utils/dateUtils';
import { storedClassWeeks } from '../../../utils/classWeeks';

/**
 * The last week number that exists in the lesson's term, or Infinity when the
 * term's dates are not loaded (a fixture, a partial row). Phase E of #168: a
 * stored week beyond the term must never be OFFERED by a picker — the backend
 * 400s it — so every session-week list is bounded here, once.
 */
function lastWeekOf(lesson: Lesson): number {
  const start = lesson.Term?.startDate;
  const end = lesson.Term?.endDate;
  return start && end ? termWeekCount(start, end) : Infinity;
}

/**
 * Weeks inside the term AND inside the class's own window (`startWeek`/`endWeek`,
 * mid-term start). A hybrid or custom-band week list can hold weeks the class no
 * longer runs (the server clamps on write, but a stored list can predate a window),
 * and no picker may offer them. `lastWeekOf` is Infinity for an unloaded term, which
 * `weekInLessonWindow` treats as "no upper bound".
 */
function withinTerm(weeks: number[], lesson: Lesson): number[] {
  const last = lastWeekOf(lesson);
  return weeks.filter((w) => w >= 1 && w <= last && weekInLessonWindow(w, lesson, last));
}

/**
 * Weeks on which the lesson actually runs a session. Mirrors the backend's
 * `lessonSessionWeeks`: HYBRID → group/individual/band weeks; a plain class with a
 * CUSTOM week list (a band's rehearsals, a fortnightly INDIVIDUAL / GROUP class) →
 * those weeks, read through utils/classWeeks.ts; otherwise null (runs every term week).
 *
 * The hybrid / custom-band lists are filtered by the class window. A regular class
 * still returns null ("every term week") ON PURPOSE — callers distinguish null — so
 * its window is NOT expressed here: pass `lessonWeekWindow(lesson, termWeekCount(...))`
 * to the picker as `windowFirst`/`windowLast` instead.
 *
 * Shared by every per-week admin dialog (replacement teacher, cancel) so the
 * pickers never offer a week the backend would reject as "no session" — an
 * exception created there would never render anywhere.
 */
export function sessionWeeksFor(lesson: Lesson): number[] | null {
  if (lesson.LessonType?.type === 'HYBRID' && lesson.HybridLessonPattern) {
    const p = lesson.HybridLessonPattern;
    const combination = p.hybridCombination ?? 'GROUP_INDIVIDUAL';
    const weeks = new Set<number>();
    // groupWeeks only meaningful for GROUP_INDIVIDUAL / GROUP_BAND (mirrors the
    // backend's guard against stale groupWeeks after a combination switch).
    if (combination === 'GROUP_INDIVIDUAL' || combination === 'GROUP_BAND') {
      for (const w of p.groupWeeks ?? []) weeks.add(w);
    }
    for (const w of p.individualWeeks ?? []) weeks.add(w);
    for (const w of p.bandWeeks ?? []) weeks.add(w);
    return withinTerm(Array.from(weeks), lesson).sort((a, b) => a - b);
  }
  // A plain class (a HYBRID with no pattern reads as every week). `?? null`: not every
  // lessons endpoint sends the rows, and a class without them runs every week.
  const stored = storedClassWeeks({
    ...lesson,
    LessonType: lesson.LessonType ?? null,
    LessonTermWeeks: lesson.LessonTermWeeks ?? null,
  });
  return stored === null ? null : withinTerm(stored, lesson);
}

export function useSessionWeeks(lesson: Lesson | undefined): number[] | null {
  return useMemo(() => (lesson ? sessionWeeksFor(lesson) : null), [lesson]);
}

/**
 * Weeks of a hybrid lesson that hold ONLY 1:1 clinics (individual weeks that are
 * not also group or band weeks). Mirrors the backend's `clinicOnlyWeeks`: a
 * per-week replacement on such a week is accepted by the schema but honoured
 * by nothing a booking runs through, so the replacement dialog locks them and
 * points at the per-booking Change Teacher instead (2026-09-06).
 * INDIVIDUAL_BAND has no clinic (#377) — its individual week is the class itself —
 * so none of its weeks are clinic weeks.
 */
export function clinicOnlyWeeksFor(lesson: Lesson): number[] {
  if (lesson.LessonType?.type !== 'HYBRID' || !lesson.HybridLessonPattern) return [];
  const p = lesson.HybridLessonPattern;
  const combination = p.hybridCombination ?? 'GROUP_INDIVIDUAL';
  if (combination === 'INDIVIDUAL_BAND') return [];
  const shared = new Set<number>();
  if (combination === 'GROUP_INDIVIDUAL' || combination === 'GROUP_BAND') {
    for (const w of p.groupWeeks ?? []) shared.add(w);
  }
  for (const w of p.bandWeeks ?? []) shared.add(w);
  return (p.individualWeeks ?? []).filter((w) => !shared.has(w)).sort((a, b) => a - b);
}
